import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  finalizeCargoHeatingOnboardingPayload,
  mergeUploadedFilesMetadata,
  normalizeUploadedFilesMetadata,
} from 'src/app/shared/helpers/cargo-heating-onboarding-payload.util';
import { mergeOnboardingModules } from 'src/app/shared/helpers/onboarding-modules.util';
import {
  BehaviorSubject,
  catchError,
  from,
  map,
  Observable,
  of,
  switchMap,
  throwError,
  timer,
} from 'rxjs';

export interface BoilerPdfStatusData {
  boiler_manufacturer?: string;
  boiler_capacity?: number;
  max_efficiency?: number;
  steam_production_at_max_efficiency?: number;
  deck_steam_pressure?: number;
  s3_url?: string;
  error?: string;
  message?: string;
}

export interface BoilerPdfStatusResponse {
  success?: boolean;
  is_successful?: boolean;
  task_id?: string;
  job_uuid?: string;
  status?: string;
  job_state?: string;
  s3_url?: string;
  object_storage_url?: string;
  data?: BoilerPdfStatusData;
  error?: string;
  message?: string;
}

export type BoilerPdfUploadResponse = BoilerPdfStatusResponse;

export interface HeatingTableCompartmentOutput {
  compartment_id?: string;
  compartmentId?: string;
  compartment?: string;
  id?: string;
  name?: string;
  volume_m3?: number;
  volumeM3?: number;
  volume?: number;
  capacity?: number;
  surface_area_m2?: number;
  surfaceAreaM2?: number;
  heating_surface_m2?: number;
  heatingSurfaceM2?: number;
  steam_demand_kg_hr?: number;
  steamDemandKgHr?: number;
  steam_demand?: number;
  max_steam?: number;
  maxSteam?: number;
}

export interface HeatingTableStatusOutput {
  thermal_parameters?: Record<string, unknown>;
  thermalParameters?: Record<string, unknown>;
  compartments?: HeatingTableCompartmentOutput[];
  compartment_metrics?: HeatingTableCompartmentOutput[];
  heating_table?: HeatingTableCompartmentOutput[];
  tables?: Array<{ columns?: string[]; rows?: unknown[]; title?: string }>;
  rows?: unknown[];
}

export interface HeatingTableStatusResponse {
  success?: boolean;
  is_successful?: boolean;
  task_id?: string;
  job_uuid?: string;
  object_storage_url?: string;
  job_state?: string;
  status?: string;
  data?: Record<string, unknown>;
  output?: HeatingTableStatusOutput | null;
  thermal_parameters?: Record<string, unknown>;
  thermalParameters?: Record<string, unknown>;
  heating_conditions?: Record<string, unknown>;
  tank_details?: Array<Record<string, unknown>>;
  tables?: Array<{ columns?: string[]; rows?: unknown[]; title?: string }>;
  s3_url?: string;
  s3_path?: string;
  info?: string;
  message?: string;
  error?: string;
}

export type HeatingTableUploadResponse = HeatingTableStatusResponse;

export interface LiquidCargoTableRowOutput {
  compartment?: string;
  compartments?: string;
  compartment_id?: string;
  compartmentId?: string;
  location_frame?: string;
  locationFrame?: string;
  location?: string;
  volume_100_m3?: number;
  volume100M3?: number;
  volume_m3?: number;
  volume?: number;
  weight_mt?: number;
  weightMt?: number;
  weight?: number;
  vcg_from_bl_m?: number;
  vcgFromBlM?: number;
  vcg?: number;
  lcg_from_midship_m?: number;
  lcgFromMidshipM?: number;
  lcg?: number;
  tcg_from_cl_m?: number;
  tcgFromClM?: number;
  tcg?: number;
  i_max_m4?: number;
  iMaxM4?: number;
  i_max?: number;
}

export interface LiquidCargoTableStatusOutput {
  rows?: LiquidCargoTableRowOutput[];
  fill_note?: string;
  fillNote?: string;
  tables?: Array<{ columns?: string[]; rows?: unknown[]; title?: string }>;
}

export interface LiquidCargoTableStatusResponse {
  success?: boolean;
  is_successful?: boolean;
  task_id?: string;
  job_uuid?: string;
  object_storage_url?: string;
  job_state?: string;
  status?: string;
  data?: Record<string, unknown>;
  output?: LiquidCargoTableStatusOutput | null;
  tables?: Array<{ columns?: string[]; rows?: unknown[]; title?: string }>;
  s3_url?: string;
  s3_path?: string;
  info?: string;
  message?: string;
  error?: string;
}

export type LiquidCargoTableUploadResponse = LiquidCargoTableStatusResponse;
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import {
  buildHeatPlanPrintPayload,
  parseHeatPlanPrintBlob,
} from 'src/app/modules/Cargo_Heating_modules/heat-plan-graph/heat-plan-print-loader.util';

@Injectable({
  providedIn: 'root',
})
export class CargoheatingCommonService {
  private get baseUrl(): string {
    return environment.apiBaseChUrl || '';
  }
  /** Apps API — /api/cargo-heating/vessel-onboarding (list, register, details, save) */
  private get appsBaseUrl(): string {
    return environment.apiBaseUrl || '';
  }
  private heatPlanData: any = null;
  private heatPlanGraphRow: any = null;
  private heatPlanGraphPayload: any = null;
  private heatPlanFormContext: any = null;
  /** When true, Forecast Temperature must not restore session draft over fresh API data. */
  private skipForecastDraftRestore = false;
  private heatPlanDataVersion = 0;
  private onboardingVesselContext: any = null;
  private readonly optimizationConfigKeyPrefix = 'ch-optimization-config-';

  private fleetViewRowData$ = new BehaviorSubject<any>(null);
  constructor(private http: HttpClient) { }

  private savedOnboardingData: any = null;

  setOnboardingVesselContext(context: any): void {
    this.onboardingVesselContext = context;
    if (context?.imo || context?.vessel_imo) {
      sessionStorage.setItem('ch-onboarding-context', JSON.stringify(context));
    }
  }

  getOnboardingVesselContext(): any {
    if (this.onboardingVesselContext) {
      return this.onboardingVesselContext;
    }
    const stored = sessionStorage.getItem('ch-onboarding-context');
    if (!stored) {
      return null;
    }
    try {
      this.onboardingVesselContext = JSON.parse(stored);
      return this.onboardingVesselContext;
    } catch {
      return null;
    }
  }

  setSavedOnboardingData(data: any): void {
    this.savedOnboardingData = null;
    try {
      sessionStorage.removeItem('ch-vessel-onboarding-data');
    } catch (_) {}
  }

  getSavedOnboardingData(): any {
    return null;
  }

  saveCargoOptimizationConfig(imo: string, config: any): void {
    if (!imo) {
      return;
    }
    try {
      localStorage.removeItem(`${this.optimizationConfigKeyPrefix}${imo}`);
    } catch (_) {}
  }

  /**
   * Apps API — Cargo Heating vessel onboarding.
   * GET  /api/cargo-heating/vessel-onboarding?session={session} — list vessels
   * GET  /api/cargo-heating/vessel-onboarding?vessel_imo={imo}&session={session} — vessel details
   * POST /api/cargo-heating/vessel-onboarding?session={session} — register / save config (multipart or JSON)
   */
  private cargoVesselOnboardingUrl(
    session?: string,
    extras?: { clientId?: number; submittedAt?: string },
  ): string {
    const resolvedSession = session ?? this.getSession();
    const params = new URLSearchParams({
      session: resolvedSession,
    });
    if (extras?.clientId != null && extras.clientId > 0) {
      params.set('client_id', String(extras.clientId));
    }
    if (extras?.submittedAt) {
      params.set('submitted_at', extras.submittedAt);
    }
    const base = (this.baseUrl || '').replace(/\/+$/, '');
    return `${base}/api/cargo-heating/vessel-onboarding?${params.toString()}`;
  }

  private readonly cargoOnboardingRootKeys = new Set([
    'submitted_at',
    'client_id',
    'vesselName',
    'vessel_name',
    'imo',
    'vessel_imo',
    'segment',
    'vesselType',
    'vessel_type',
    'onboarding_status',
    'pipelineLineWorkbook',
    'boilerGraphFileName',
    'steamTableFileName',
    'liquidCargoFileName',
  ]);

  private readonly cargoOnboardingCamelToSnake: Record<string, string> = {
    vesselName: 'vessel_name',
    vesselType: 'vessel_type',
    tankerType: 'tanker_type',
    numberOfTanks: 'number_of_tanks',
    numberOfCargoTanks: 'number_of_cargo_tanks',
    numberOfBoilers: 'number_of_boilers',
    boilerMake: 'boiler_make',
    boilerCapacity: 'boiler_capacity',
    auxBoilerFuelConsumptionKgHr: 'aux_boiler_fuel_consumption_kg_hr',
    maxEfficiencyPercent: 'max_efficiency_percent',
    steamProductionKgHr: 'steam_production_kg_hr',
    boilerLoadPercentage: 'boiler_load_percentage',
    deckSteamPressure: 'deck_steam_pressure',
    compositeBoilerConsumptionPerDay: 'composite_boiler_consumption_per_day',
    compositeBoilerFuelConsumptionKgHr: 'composite_boiler_fuel_consumption_kg_hr',
    steamOutputOilFiredSectionKgHr: 'steam_output_oil_fired_section_kg_hr',
    steamOutputExhaustSectionMeMotorKgHr:
      'steam_output_exhaust_section_me_motor_kg_hr',
    steamOutputExhaustSectionAeMotorKgHr:
      'steam_output_exhaust_section_ae_motor_kg_hr',
    aeBoilerConsumptionMt: 'ae_boiler_consumption_mt',
    deckHeaterCapacityKw: 'deck_heater_capacity_kw',
    deckHeatExchangerBunkerConsumptionMt:
      'deck_heat_exchanger_bunker_consumption_mt',
    egbCapacityKgHr: 'egb_capacity_kg_hr',
    egbExhaustGasQuantityKgHr: 'egb_exhaust_gas_quantity_kg_hr',
    egbGasTempInletDegC: 'egb_gas_temp_inlet_deg_c',
    egbGasTempOutletDegC: 'egb_gas_temp_outlet_deg_c',
    egcsScrubber: 'egcs_scrubber',
    heatingArrangement: 'heating_arrangement',
    pipelineSegmentName: 'pipeline_segment_name',
    pipelinePosition: 'pipeline_position',
    pipelineNominalDiameterMm: 'pipeline_nominal_diameter_mm',
    pipelineInsideDiameterMm: 'pipeline_inside_diameter_mm',
    pipelineOutsideDiameterMm: 'pipeline_outside_diameter_mm',
    pipelineLengthM: 'pipeline_length_m',
    pipelineThicknessMm: 'pipeline_thickness_mm',
    pipelineSections: 'pipeline_sections',
    pipelineMainLineThickness1Mm: 'pipeline_main_line_thickness_1_mm',
    pipelineMainLineThickness2Mm: 'pipeline_main_line_thickness_2_mm',
    pipelineMainLineThickness3Mm: 'pipeline_main_line_thickness_3_mm',
    pipelineBranchLineThickness1Mm: 'pipeline_branch_line_thickness_1_mm',
    pipelineBranchLineThickness2Mm: 'pipeline_branch_line_thickness_2_mm',
    pipelineBranchLineThickness3Mm: 'pipeline_branch_line_thickness_3_mm',
    pipelineInEngineRoomThicknessMm: 'pipeline_in_engine_room_thickness_mm',
    pipelineMainLineLength1M: 'pipeline_main_line_length_1_m',
    pipelineMainLineLength2M: 'pipeline_main_line_length_2_m',
    pipelineMainLineLength3M: 'pipeline_main_line_length_3_m',
    pipelineBranchLineLength1M: 'pipeline_branch_line_length_1_m',
    pipelineBranchLineLength2M: 'pipeline_branch_line_length_2_m',
    pipelineBranchLineLength3M: 'pipeline_branch_line_length_3_m',
    pipelineInEngineRoomLengthM: 'pipeline_in_engine_room_length_m',
    steamInsulationMainLine: 'steam_insulation_main_line',
    steamInsulationBranchLine: 'steam_insulation_branch_line',
    steamInsulationInEngineRoom: 'steam_insulation_in_engine_room',
    pipelineLineExcelFileName: 'pipeline_line_excel_file_name',
    pipelineLineSheetName: 'pipeline_line_sheet_name',
    steamPipesInsulation: 'steam_pipes_insulation',
    boilerGraphApplied: 'boiler_graph_applied',
    boilerGraphFileName: 'boiler_graph_file_name',
    steamTableApplied: 'steam_table_applied',
    steamTableFileName: 'steam_table_file_name',
    liquidCargoApplied: 'liquid_cargo_applied',
    liquidCargoFileName: 'liquid_cargo_file_name',
    liquidCargoFillNote: 'liquid_cargo_fill_note',
    liquidCargoTotals: 'liquid_cargo_totals',
    liquidCargo: 'liquid_cargo',
    cargoClassification: 'cargo_classification',
    relativeDensitySg: 'relative_density_sg',
    heatCapacitySh: 'heat_capacity_sh',
    initialProductTemperature: 'initial_product_temperature',
    maintenanceProductTemperature: 'maintenance_product_temperature',
    targetProductTemperature: 'target_product_temperature',
    heatingWindowHours: 'heating_window_hours',
    slopInitialTemperature: 'slop_initial_temperature',
    slopTargetTemperature: 'slop_target_temperature',
    slopDurationHours: 'slop_duration_hours',
    waterAmbientTemperature: 'water_ambient_temperature',
    airAmbientTemperature: 'air_ambient_temperature',
    manifoldSteamPressure: 'manifold_steam_pressure',
    heatingTableJobUuid: 'heating_table_job_uuid',
    heatingTableS3Url: 'heating_table_s3_url',
    liquidCargoJobUuid: 'liquid_cargo_job_uuid',
    liquidCargoS3Url: 'liquid_cargo_s3_url',
    uploaded_files: 'uploaded_files',
    uploadedFiles: 'uploaded_files',
  };

  private toSnakeCaseKey(key: string): string {
    if (this.cargoOnboardingCamelToSnake[key]) {
      return this.cargoOnboardingCamelToSnake[key];
    }
    if (key.includes('_')) {
      return key;
    }
    return key
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2')
      .toLowerCase();
  }

  private mapCargoOnboardingFieldsToSnakeCase(
    source: Record<string, unknown>,
  ): Record<string, unknown> {
    const mapped: Record<string, unknown> = {};
    Object.entries(source).forEach(([key, value]) => {
      if (value === undefined) {
        return;
      }
      mapped[this.toSnakeCaseKey(key)] = value;
    });
    return mapped;
  }

  /**
   * Flat snake_case payload matching GET /api/cargo-heating/vessel-onboarding rows.
   */
  buildCargoVesselOnboardingApiPayload(
    config: Record<string, unknown>,
  ): Record<string, unknown> {
    const context = this.getOnboardingVesselContext();
    const client_id = Number(
      config['client_id'] ?? context?.client_id ?? 0,
    );
    const submitted_at =
      (config['submitted_at'] as string) || new Date().toISOString();

    const cargoHeatingRaw: Record<string, unknown> = { ...config };
    this.cargoOnboardingRootKeys.forEach((key) => delete cargoHeatingRaw[key]);

    const normalized = this.normalizeCargoOnboardingPayload(cargoHeatingRaw);
    const snakeCargo = this.mapCargoOnboardingFieldsToSnakeCase(normalized);

    const payload: Record<string, unknown> = {
      ...snakeCargo,
      client_id,
      submitted_at,
      onboarding_modules: mergeOnboardingModules(
        config['onboarding_modules'],
        config['onboardingModules'],
        'cargo-heating',
      ),
      vessel_name: String(
        config['vesselName'] ??
        config['vessel_name'] ??
        context?.vessel_name ??
        '',
      ),
      imo: String(
        config['imo'] ?? config['vessel_imo'] ?? context?.imo ?? '',
      ),
      vessel_type: String(
        config['vesselType'] ??
        config['vessel_type'] ??
        context?.vessel_type ??
        '',
      ),
      segment: String(config['segment'] ?? context?.segment ?? ''),
    };

    return payload;
  }

  private normalizeCargoOnboardingPayload(
    payload: Record<string, unknown>,
  ): Record<string, unknown> {
    const numericRootKeys = [
      'numberOfTanks',
      'numberOfCargoTanks',
      'numberOfBoilers',
      'boilerCapacity',
      'maxEfficiencyPercent',
      'steamProductionKgHr',
      'boilerLoadPercentage',
      'deckSteamPressure',
      'compositeBoilerConsumptionPerDay',
      'compositeBoilerFuelConsumptionKgHr',
      'steamOutputOilFiredSectionKgHr',
      'steamOutputExhaustSectionMeMotorKgHr',
      'steamOutputExhaustSectionAeMotorKgHr',
      'aeBoilerConsumptionMt',
      'auxBoilerFuelConsumptionKgHr',
      'deckHeaterCapacityKw',
      'deckHeatExchangerBunkerConsumptionMt',
      'pipelineLengthM',
      'pipelineThicknessMm',
      'pipelineMainLineThickness1Mm',
      'pipelineMainLineThickness2Mm',
      'pipelineMainLineThickness3Mm',
      'pipelineBranchLineThickness1Mm',
      'pipelineBranchLineThickness2Mm',
      'pipelineBranchLineThickness3Mm',
      'pipelineInEngineRoomThicknessMm',
      'pipelineMainLineLength1M',
      'pipelineMainLineLength2M',
      'pipelineMainLineLength3M',
      'pipelineBranchLineLength1M',
      'pipelineBranchLineLength2M',
      'pipelineBranchLineLength3M',
      'pipelineInEngineRoomLengthM',
      'pipelineNominalDiameterMm',
      'pipelineInsideDiameterMm',
      'pipelineOutsideDiameterMm',
      'egbCapacityKgHr',
      'egbExhaustGasQuantityKgHr',
      'egbGasTempInletDegC',
      'egbGasTempOutletDegC',
    ];

    numericRootKeys.forEach((key) => {
      const value = payload[key];
      if (value === null || value === undefined || value === '') {
        return;
      }
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) {
        payload[key] = parsed;
      }
    });

    if (
      payload['deckHeaterCapacityKw'] != null &&
      payload['deckHeaterCapacityKw'] !== '' &&
      (payload['deckHeatExchangerBunkerConsumptionMt'] == null ||
        payload['deckHeatExchangerBunkerConsumptionMt'] === '')
    ) {
      payload['deckHeatExchangerBunkerConsumptionMt'] =
        payload['deckHeaterCapacityKw'];
    }
    if (
      payload['deckHeatExchangerBunkerConsumptionMt'] != null &&
      payload['deckHeatExchangerBunkerConsumptionMt'] !== '' &&
      (payload['deckHeaterCapacityKw'] == null ||
        payload['deckHeaterCapacityKw'] === '')
    ) {
      payload['deckHeaterCapacityKw'] =
        payload['deckHeatExchangerBunkerConsumptionMt'];
    }

    if (Array.isArray(payload['tanks'])) {
      payload['tanks'] = (payload['tanks'] as Record<string, unknown>[]).map(
        (tank) => this.normalizeTankForApi(tank),
      );
    }

    if (Array.isArray(payload['liquidCargo'])) {
      payload['liquidCargo'] = (
        payload['liquidCargo'] as Record<string, unknown>[]
      ).map((row) => ({
        ...row,
        volume100M3:
          row['volume100M3'] == null || row['volume100M3'] === ''
            ? row['volume100M3']
            : Number(row['volume100M3']),
        weightMt:
          row['weightMt'] == null || row['weightMt'] === ''
            ? row['weightMt']
            : Number(row['weightMt']),
        vcgFromBlM:
          row['vcgFromBlM'] == null || row['vcgFromBlM'] === ''
            ? row['vcgFromBlM']
            : Number(row['vcgFromBlM']),
        lcgFromMidshipM:
          row['lcgFromMidshipM'] == null || row['lcgFromMidshipM'] === ''
            ? row['lcgFromMidshipM']
            : Number(row['lcgFromMidshipM']),
        tcgFromClM:
          row['tcgFromClM'] == null || row['tcgFromClM'] === ''
            ? row['tcgFromClM']
            : Number(row['tcgFromClM']),
        iMaxM4:
          row['iMaxM4'] == null || row['iMaxM4'] === ''
            ? row['iMaxM4']
            : Number(row['iMaxM4']),
      }));
    }

    if (payload['liquidCargoTotals'] && typeof payload['liquidCargoTotals'] === 'object') {
      const totals = payload['liquidCargoTotals'] as Record<string, unknown>;
      payload['liquidCargoTotals'] = {
        volume100M3:
          totals['volume100M3'] == null
            ? totals['volume100M3']
            : Number(totals['volume100M3']),
        weightMt:
          totals['weightMt'] == null ? totals['weightMt'] : Number(totals['weightMt']),
      };
    }

    return payload;
  }

  private parseOptionalNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * Map tank form fields to the backend contract.
   * Computes heatingRatio from heating surface when needed (DB stores heatingRatio).
   */
  private normalizeTankForApi(
    tank: Record<string, unknown>,
  ): Record<string, unknown> {
    const name = String(tank['name'] ?? tank['tankName'] ?? '').trim();
    const capacity = this.parseOptionalNumber(
      tank['capacity'] ?? tank['tankCapacityM3'] ?? tank['capacityMt'],
    );

    let heatingSurfaceM2 = this.parseOptionalNumber(
      tank['heatingSurfaceM2'] ??
      tank['heating_surface_m2'] ??
      tank['heatingSurface'],
    );
    let heatingRatio = this.parseOptionalNumber(
      tank['heatingRatio'] ?? tank['heating_ratio'],
    );
    const maxSteam = this.parseOptionalNumber(
      tank['maxSteam'] ??
      tank['max_steam'] ??
      tank['maxSteamKgHr'] ??
      tank['maxSteamMt'],
    );
    const stepRequirement =
      tank['stepRequirement'] ?? tank['step_requirement'] ?? '';

    if (
      heatingSurfaceM2 == null &&
      heatingRatio != null &&
      capacity != null &&
      capacity > 0
    ) {
      heatingSurfaceM2 = Math.round(capacity * heatingRatio * 100) / 100;
    }
    if (
      heatingRatio == null &&
      heatingSurfaceM2 != null &&
      capacity != null &&
      capacity > 0
    ) {
      heatingRatio =
        Math.round((heatingSurfaceM2 / capacity) * 10000) / 10000;
    }

    return {
      name,
      capacity,
      heatingRatio,
      heatingSurfaceM2,
      maxSteam,
      maxSteamKgHr: maxSteam,
      stepRequirement,
      heating_surface_m2: heatingSurfaceM2,
      max_steam: maxSteam,
      max_steam_consumption_kg_hr: maxSteam,
      step_requirement: stepRequirement,
    };
  }

  private buildUploadedFilesMetadata(
    files: Array<{ key: string; file: File | null | undefined }>,
    metadataByField?: Map<
      string,
      {
        field: string;
        file_name: string;
        s3_url?: string;
        mime_type?: string;
        size_bytes?: number;
      }
    >,
  ): Array<{
    field: string;
    file_name: string;
    s3_url?: string;
    mime_type?: string;
    size_bytes?: number;
  }> {
    return files
      .filter((entry) => !!entry.file)
      .map((entry) => {
        const metadata = metadataByField?.get(entry.key);
        const item: {
          field: string;
          file_name: string;
          s3_url?: string;
          mime_type?: string;
          size_bytes?: number;
        } = {
          field: entry.key,
          file_name: entry.file!.name,
        };
        const s3Url = String(metadata?.s3_url ?? '').trim();
        if (s3Url) {
          item.s3_url = s3Url;
        }
        const mimeType = String(entry.file!.type || metadata?.mime_type || '').trim();
        if (mimeType) {
          item.mime_type = mimeType;
        }
        if (Number.isFinite(entry.file!.size)) {
          item.size_bytes = entry.file!.size;
        } else if (metadata?.size_bytes != null) {
          item.size_bytes = metadata.size_bytes;
        }
        return item;
      });
  }

  private mapUploadedFilesMetadata(
    entries: Array<Record<string, unknown>>,
  ): Array<{ field: string; file_name: string; s3_url?: string }> {
    return entries
      .map((entry) => {
        const field = String(entry['field'] ?? '').trim();
        const file_name = String(
          entry['file_name'] ?? entry['fileName'] ?? '',
        ).trim();
        if (!field || !file_name) {
          return null;
        }
        const item: { field: string; file_name: string; s3_url?: string } = {
          field,
          file_name,
        };
        const s3Url = String(entry['s3_url'] ?? entry['s3Url'] ?? '').trim();
        if (s3Url) {
          item.s3_url = s3Url;
        }
        return item;
      })
      .filter((entry): entry is NonNullable<typeof entry> => !!entry);
  }

  private buildMultipartOnboardingFormData(
    payload: Record<string, unknown>,
    files: {
      boilerGraphFile?: File | null;
      boilerGraphJsonFile?: File | null;
      steamTableFile?: File | null;
      liquidCargoFile?: File | null;
    },
  ): FormData {
    const formData = new FormData();
    const fileEntries: Array<{ key: string; file: File | null | undefined }> = [
      { key: 'steam_table', file: files.steamTableFile },
      { key: 'boiler_graph', file: files.boilerGraphFile },
      { key: 'boiler_graph_json', file: files.boilerGraphJsonFile },
      { key: 'liquid_cargo', file: files.liquidCargoFile },
    ];

    for (const { key, file } of fileEntries) {
      if (file) {
        formData.append(key, file, file.name);
      }
    }

    formData.append('data', JSON.stringify(payload));
    return formData;
  }

  private stripLegacyFilePayloadKeys(payload: Record<string, unknown>): void {
    delete payload['file_attachments'];
    const legacyKeys = [
      'steam_table_base64',
      'steam_table_file_name',
      'steam_table_mime_type',
      'boiler_graph_base64',
      'boiler_graph_file_name',
      'boiler_graph_mime_type',
      'boiler_graph_json_base64',
      'boiler_graph_json_file_name',
      'boiler_graph_json_mime_type',
      'liquid_cargo_base64',
      'liquid_cargo_file_name',
      'liquid_cargo_mime_type',
    ];
    legacyKeys.forEach((key) => delete payload[key]);
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result ?? '');
        const base64 = result.includes(',')
          ? result.split(',')[1]
          : result;
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private async attachUploadedFilesToPayload(
    payload: Record<string, unknown>,
    files: {
      boilerGraphFile?: File | null;
      boilerGraphJsonFile?: File | null;
      steamTableFile?: File | null;
      liquidCargoFile?: File | null;
      json?: Record<string, unknown>;
    },
  ): Promise<Record<string, unknown>> {
    const attachments: Record<string, unknown> = {};
    const uploadedFiles: Array<{
      field: string;
      file_name: string;
      s3_url?: string;
    }> = [];
    const metadataByField = new Map(
      (
        (files.json?.['uploaded_files'] as Array<Record<string, unknown>>) ?? []
      ).map((entry) => [String(entry['field'] ?? ''), entry]),
    );

    const entries: Array<{ key: string; file: File | null | undefined }> = [
      { key: 'steam_table', file: files.steamTableFile },
      { key: 'boiler_graph', file: files.boilerGraphFile },
      { key: 'boiler_graph_json', file: files.boilerGraphJsonFile },
      { key: 'liquid_cargo', file: files.liquidCargoFile },
    ];

    for (const { key, file } of entries) {
      if (!file) {
        continue;
      }

      const data_base64 = await this.readFileAsBase64(file);
      const mime_type =
        file.type ||
        String(metadataByField.get(key)?.['mime_type'] ?? '') ||
        'application/octet-stream';
      const metadata = metadataByField.get(key);
      const uploadedEntry: {
        field: string;
        file_name: string;
        s3_url?: string;
      } = {
        field: key,
        file_name: file.name,
      };
      const s3Url = String(metadata?.['s3_url'] ?? metadata?.['s3Url'] ?? '')
        .trim();
      if (s3Url) {
        uploadedEntry.s3_url = s3Url;
      }
      uploadedFiles.push(uploadedEntry);
      attachments[key] = {
        file_name: file.name,
        mime_type,
        size_bytes: file.size,
        data_base64,
      };
      payload[`${key}_file_name`] = file.name;
      payload[`${key}_mime_type`] = mime_type;
      payload[`${key}_base64`] = data_base64;
    }

    if (uploadedFiles.length) {
      payload['uploaded_files'] = uploadedFiles;
      payload['file_attachments'] = attachments;
    } else if (Array.isArray(files.json?.['uploaded_files'])) {
      payload['uploaded_files'] = this.mapUploadedFilesMetadata(
        files.json?.['uploaded_files'] as Array<Record<string, unknown>>,
      );
    } else {
      payload['uploaded_files'] = [];
    }

    return payload;
  }

  private prepareCargoOnboardingSubmitPayload(
    json: Record<string, unknown>,
  ): Record<string, unknown> {
    if ('vessel_name' in json) {
      return finalizeCargoHeatingOnboardingPayload(json);
    }
    return finalizeCargoHeatingOnboardingPayload(
      this.buildCargoVesselOnboardingApiPayload(json),
    );
  }

  private mergeNewUploadFilesIntoPayload(
    payload: Record<string, unknown>,
    files: {
      boilerGraphFile?: File | null;
      boilerGraphJsonFile?: File | null;
      steamTableFile?: File | null;
      liquidCargoFile?: File | null;
      json?: Record<string, unknown>;
    },
  ): Record<string, unknown> {
    const existing = normalizeUploadedFilesMetadata(
      payload['uploaded_files'] ??
      files.json?.['uploaded_files'] ??
      [],
    );
    const metadataByField = new Map(
      existing.map((entry) => [entry.field, entry]),
    );
    const fileEntries: Array<{ key: string; file: File | null | undefined }> = [
      { key: 'steam_table', file: files.steamTableFile },
      { key: 'boiler_graph', file: files.boilerGraphFile },
      { key: 'boiler_graph_json', file: files.boilerGraphJsonFile },
      { key: 'liquid_cargo', file: files.liquidCargoFile },
    ];
    const updates = this.buildUploadedFilesMetadata(fileEntries, metadataByField)
      .map((entry) => {
        const file = fileEntries.find((item) => item.key === entry.field)?.file;
        if (!file) {
          return entry;
        }
        return {
          ...entry,
          mime_type: file.type || entry.mime_type,
          size_bytes: file.size,
        };
      });

    if (updates.length) {
      payload['uploaded_files'] = mergeUploadedFilesMetadata(existing, updates);
    } else if (existing.length) {
      payload['uploaded_files'] = existing;
    }

    return payload;
  }

  private buildSavePayloadWithFiles(payload: {
    json: Record<string, unknown>;
    boilerGraphFile?: File | null;
    boilerGraphJsonFile?: File | null;
    steamTableFile?: File | null;
    liquidCargoFile?: File | null;
  }): Record<string, unknown> {
    const body = this.prepareCargoOnboardingSubmitPayload(payload.json);
    this.stripLegacyFilePayloadKeys(body);
    return this.mergeNewUploadFilesIntoPayload(body, {
      ...payload,
      json: payload.json,
    });
  }

  private postCargoVesselOnboardingMultipart(
    formData: FormData,
    extras?: { clientId?: number; submittedAt?: string },
  ): Observable<any> {
    return this.http
      .post(this.cargoVesselOnboardingUrl(undefined, extras), formData)
      .pipe(catchError(this.errorHandler));
  }

  private postCargoVesselOnboardingJson(
    body: Record<string, unknown>,
    extras?: { clientId?: number; submittedAt?: string },
  ): Observable<any> {
    return this.http
      .post(this.cargoVesselOnboardingUrl(undefined, extras), body, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(this.errorHandler));
  }

  /** List vessels onboarded for Cargo Heating. */
  getCargoVesselOnboardingVessels(): Observable<any[]> {
    return this.http
      .get<any[]>(this.cargoVesselOnboardingUrl())
      .pipe(catchError(this.errorHandler));
  }

  /** Fetch onboarding details for a single vessel by IMO via /cargoheating/onboarding/get_data. */
  getCargoVesselOnboardingDetails(vesselImo: string | number): Observable<any> {
    return this.getCargoHeatingOnboardingData(vesselImo);
  }

  /**
   * Check if vessel is previously onboarded for Cargo Heating using get_data endpoint.
   * GET /cargoheating/onboarding/get_data?vessel_imo={numeric_imo}
   */
  checkCargoVesselOnboarded(
    vesselImo: string | number,
  ): Observable<{ previously_onboarded?: boolean; data?: any; [key: string]: any }> {
    return this.getCargoHeatingOnboardingData(vesselImo).pipe(
      map((res: any) => {
        const data = res?.data || res;
        const exists = !!(
          data &&
          !data.error &&
          (data.vessel_name ||
            data.vessel_imo ||
            data.imo ||
            data.main_boiler ||
            data.pipeline_configuration ||
            data.composite_boiler ||
            data.egb_economizer ||
            data.boiler_capacity ||
            data.heating_arrangement)
        );
        return { previously_onboarded: exists, data };
      }),
      catchError(() => of({ previously_onboarded: false })),
    );
  }

  /**
   * Fetch saved onboarding configuration data for a vessel.
   * GET /cargoheating/onboarding/get_data?vessel_imo={numeric_imo}
   */
  getCargoHeatingOnboardingData(
    vesselImo: string | number,
  ): Observable<any> {
    const numericImo = Number(vesselImo);
    const resolvedImo = Number.isFinite(numericImo) ? numericImo : vesselImo;
    const base = (this.baseUrl || '').replace(/\/+$/, '');
    const url = `${base}/cargoheating/onboarding/get_data?vessel_imo=${encodeURIComponent(String(resolvedImo))}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  /**
   * Upload a supporting document for one onboarding section (overwrites the previous one).
   * POST /cargoheating/onboarding/upload_document  (multipart: imo, doc_type, document)
   */
  uploadOnboardingDocument(
    imo: string | number,
    docType: string,
    document: File,
  ): Observable<any> {
    const base = (this.baseUrl || '').replace(/\/+$/, '');
    const formData = new FormData();
    formData.append('imo', String(imo));
    formData.append('doc_type', docType);
    formData.append('document', document, document.name);
    return this.http
      .post<any>(`${base}/cargoheating/onboarding/upload_document`, formData)
      .pipe(catchError(this.errorHandler));
  }

  /**
   * Fetch the stored document for one onboarding section as a blob (404 when none exists).
   * GET /cargoheating/onboarding/get_document?imo={imo}&doc_type={doc_type}
   */
  getOnboardingDocument(
    imo: string | number,
    docType: string,
  ): Observable<HttpResponse<Blob>> {
    const base = (this.baseUrl || '').replace(/\/+$/, '');
    return this.http
      .get(`${base}/cargoheating/onboarding/get_document`, {
        params: { imo: String(imo), doc_type: docType },
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(catchError(this.errorHandler));
  }

  /**
   * Check whether a section document exists without downloading it (HEAD on get_document).
   */
  checkOnboardingDocument(
    imo: string | number,
    docType: string,
  ): Observable<HttpResponse<unknown>> {
    const base = (this.baseUrl || '').replace(/\/+$/, '');
    return this.http
      .head(`${base}/cargoheating/onboarding/get_document`, {
        params: { imo: String(imo), doc_type: docType },
        observe: 'response',
      })
      .pipe(catchError(this.errorHandler));
  }

  /**
   * Fetch default pipe configuration from DB for a given segment.
   * GET /cargoheating/get_pipe_configuration?segment={segment}&pipe_configuration=pipe_configuration
   */
  getPipeConfiguration(segment: string): Observable<any> {
    const base = (this.baseUrl || '').replace(/\/+$/, '');
    const url = `${base}/cargoheating/get_pipe_configuration`;
    const params = {
      segment: segment || '',
      pipe_configuration: 'pipe_configuration',
    };
    return this.http.get<any>(url, { params }).pipe(catchError(this.errorHandler));
  }

  /**
   * Register or update a vessel for Cargo Heating onboarding via Cargo Heating service.
   * POST /cargoheating/onboarding/onboard_new_vessel
   */
  postCargoVesselOnboardingRegistration(
    payload: any,
  ): Observable<any> {
    let body: any = payload;
    if (typeof payload === 'string' || typeof payload === 'number') {
      const numericImo = Number(String(payload).replace(/\D/g, ''));
      body = {
        vessel_imo: Number.isFinite(numericImo) ? numericImo : payload,
      };
    } else if (typeof payload === 'object' && payload !== null) {
      body = { ...payload };
      if (body.vessel_imo != null) {
        const numericImo = Number(String(body.vessel_imo).replace(/\D/g, ''));
        body.vessel_imo = Number.isFinite(numericImo) ? numericImo : body.vessel_imo;
      }
      if (body.tank_count != null) {
        const numericCount = Number(body.tank_count);
        body.tank_count = Number.isFinite(numericCount) ? numericCount : body.tank_count;
      }
    }
    const base = (this.baseUrl || '').replace(/\/+$/, '');
    const url = `${base}/cargoheating/onboarding/onboard_vessel`;
    return this.http
      .post(url, body, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(this.errorHandler));
  }

  /**
   * Persist cargo heating onboarding configuration.
   * POST /api/cargo-heating/vessel-onboarding?session={session}
   * Multipart when new files are attached (`data` JSON + file fields); otherwise JSON body.
   */
  postCargoOnboardingSaveConfig(payload: {
    json: Record<string, unknown>;
    boilerGraphFile?: File | null;
    boilerGraphJsonFile?: File | null;
    steamTableFile?: File | null;
    liquidCargoFile?: File | null;
  }): Observable<any> {
    const context = this.getOnboardingVesselContext();
    if (!payload.json['client_id'] && context?.client_id) {
      payload.json['client_id'] = context.client_id;
    }
    const clientId = Number(
      payload.json['client_id'] ?? context?.client_id ?? 0,
    );
    if (!clientId) {
      return throwError(
        () =>
          new Error(
            'client_id is required. Return to onboarding and select a client.',
          ),
      );
    }
    if (!payload.json['submitted_at']) {
      payload.json['submitted_at'] = new Date().toISOString();
    }

    const body = this.buildSavePayloadWithFiles(payload);
    const resolvedClientId = Number(body['client_id'] ?? clientId);
    const submittedAt = String(body['submitted_at'] ?? payload.json['submitted_at']);
    const extras = { clientId: resolvedClientId, submittedAt };
    const hasNewFiles = !!(
      payload.boilerGraphFile ||
      payload.boilerGraphJsonFile ||
      payload.steamTableFile ||
      payload.liquidCargoFile
    );

    if (hasNewFiles) {
      const formData = this.buildMultipartOnboardingFormData(body, payload);
      return this.postCargoVesselOnboardingMultipart(formData, extras);
    }

    return this.postCargoVesselOnboardingJson(body, extras);
  }

  /** POST /scperformance/upload_boiler_table — multipart boiler graph PDF/image upload. */
  uploadBoilerPdf(file: File): Observable<BoilerPdfStatusResponse> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const url = `${this.appsBaseUrl}/scperformance/upload_boiler_table`;
    return this.http
      .post<BoilerPdfUploadResponse>(url, formData)
      .pipe(
        map((response) => this.normalizeBoilerPdfStatusResponse(response)),
        catchError(this.errorHandler),
      );
  }

  /**
   * True when upload/status response already contains boiler metadata
   * (sync upload: status completed + data payload).
   */
  isBoilerPdfResponseReady(response: BoilerPdfStatusResponse): boolean {
    if (!this.hasBoilerPdfPayload(response)) {
      return false;
    }
    if (response.success === false || response.is_successful === false) {
      return false;
    }
    const state = String(
      response.status ?? response.job_state ?? '',
    ).toLowerCase();
    if (state === 'failed' || state === 'error' || state === 'not_found') {
      return false;
    }
    if (
      !state ||
      state === 'completed' ||
      state === 'success' ||
      state === 'done'
    ) {
      return true;
    }
    return false;
  }

  /** GET /scperformance/boiler_table_status/{task_id} */
  getBoilerPdfStatus(taskId: string): Observable<BoilerPdfStatusResponse> {
    const url = `${this.appsBaseUrl}/scperformance/boiler_table_status/${encodeURIComponent(taskId)}`;
    return this.http
      .get<BoilerPdfStatusResponse>(url)
      .pipe(
        map((response) => this.normalizeBoilerPdfStatusResponse(response)),
        catchError(this.errorHandler),
      );
  }

  /**
   * Poll boiler PDF processing until status is completed or failed.
   * Default interval 4s (within 3–5s requirement).
   */
  /** POST /scperformance/upload_heating_table — multipart heating table PDF/image upload. */
  uploadHeatingTable(file: File): Observable<HeatingTableStatusResponse> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const url = `${this.appsBaseUrl}/scperformance/upload_heating_table`;
    return this.http
      .post<HeatingTableUploadResponse>(url, formData)
      .pipe(
        map((response) => this.normalizeHeatingTableStatusResponse(response)),
        catchError(this.errorHandler),
      );
  }

  /**
   * True when upload/status response already contains heating_conditions + tank_details
   * (sync upload: status completed + data payload).
   */
  isHeatingTableResponseReady(
    response: HeatingTableStatusResponse,
  ): boolean {
    if (!this.hasHeatingTablePayload(response)) {
      return false;
    }
    if (response.success === false || response.is_successful === false) {
      return false;
    }
    const state = String(
      response.status ?? response.job_state ?? '',
    ).toLowerCase();
    if (state === 'failed' || state === 'error' || state === 'not_found') {
      return false;
    }
    if (
      !state ||
      state === 'completed' ||
      state === 'success' ||
      state === 'done'
    ) {
      return true;
    }
    return false;
  }

  /** GET /scperformance/heating_table_status/{job_uuid} */
  getHeatingTableStatus(jobUuid: string): Observable<HeatingTableStatusResponse> {
    const url = `${this.appsBaseUrl}/scperformance/heating_table_status/${encodeURIComponent(jobUuid)}`;
    return this.http
      .get<HeatingTableStatusResponse>(url)
      .pipe(
        map((response) => this.normalizeHeatingTableStatusResponse(response)),
        catchError(this.errorHandler),
      );
  }

  /**
   * Poll heating table processing until completed payload or terminal failure.
   */
  pollHeatingTableUntilComplete(
    jobUuid: string,
    intervalMs = 4000,
    maxAttempts = 90,
  ): Observable<HeatingTableStatusResponse> {
    return this.pollStatusUntilComplete(
      () => this.getHeatingTableStatus(jobUuid),
      (response) => this.resolveHeatingTablePollState(response),
      intervalMs,
      maxAttempts,
      'Heating table processing timed out. Please try uploading again.',
    );
  }

  /** POST /scperformance/upload_liquid_cargo_table — multipart liquid cargo PDF/image upload. */
  uploadLiquidCargoTable(file: File): Observable<LiquidCargoTableStatusResponse> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const url = `${this.appsBaseUrl}/scperformance/upload_liquid_cargo_table`;
    return this.http
      .post<LiquidCargoTableUploadResponse>(url, formData)
      .pipe(
        map((response) => this.normalizeLiquidCargoStatusResponse(response)),
        catchError(this.errorHandler),
      );
  }

  /**
   * True when upload/status response already contains parsed table rows
   * (sync upload: status completed + data.tables).
   */
  isLiquidCargoTableResponseReady(
    response: LiquidCargoTableStatusResponse,
  ): boolean {
    if (!this.hasLiquidCargoPayload(response)) {
      return false;
    }
    if (response.success === false || response.is_successful === false) {
      return false;
    }
    const state = String(
      response.status ?? response.job_state ?? '',
    ).toLowerCase();
    if (state === 'failed' || state === 'error' || state === 'not_found') {
      return false;
    }
    if (
      !state ||
      state === 'completed' ||
      state === 'success' ||
      state === 'done'
    ) {
      return true;
    }
    return false;
  }

  /** GET /scperformance/liquid_cargo_table_status/{job_uuid} */
  getLiquidCargoTableStatus(
    jobUuid: string,
  ): Observable<LiquidCargoTableStatusResponse> {
    const url = `${this.appsBaseUrl}/scperformance/liquid_cargo_table_status/${encodeURIComponent(jobUuid)}`;
    return this.http
      .get<LiquidCargoTableStatusResponse>(url)
      .pipe(
        map((response) => this.normalizeLiquidCargoStatusResponse(response)),
        catchError(this.errorHandler),
      );
  }

  /**
   * Poll liquid cargo table processing until completed payload or terminal failure.
   */
  pollLiquidCargoTableUntilComplete(
    jobUuid: string,
    intervalMs = 4000,
    maxAttempts = 90,
  ): Observable<LiquidCargoTableStatusResponse> {
    return this.pollStatusUntilComplete(
      () => this.getLiquidCargoTableStatus(jobUuid),
      (response) => this.resolveLiquidCargoPollState(response),
      intervalMs,
      maxAttempts,
      'Liquid cargo table processing timed out. Please try uploading again.',
    );
  }

  pollBoilerPdfUntilComplete(
    taskId: string,
    intervalMs = 4000,
    maxAttempts = 90,
  ): Observable<BoilerPdfStatusResponse> {
    return this.pollStatusUntilComplete(
      () => this.getBoilerPdfStatus(taskId),
      (response) => this.resolveBoilerPdfPollState(response),
      intervalMs,
      maxAttempts,
      'Boiler PDF processing timed out. Please try uploading again.',
    );
  }

  private normalizeBoilerPdfStatusResponse(
    raw: BoilerPdfStatusResponse,
  ): BoilerPdfStatusResponse {
    const nested = raw?.data as Record<string, unknown> | undefined;
    if (!nested || typeof nested !== 'object' || Array.isArray(nested)) {
      return raw;
    }

    const data = { ...nested } as BoilerPdfStatusData;
    const topS3 =
      raw.s3_url ||
      raw.object_storage_url ||
      (typeof nested['s3_url'] === 'string' ? nested['s3_url'] : undefined);
    if (topS3 && !data.s3_url) {
      data.s3_url = topS3;
    }

    const status =
      raw.status ??
      raw.job_state ??
      (typeof nested['status'] === 'string' ? nested['status'] : undefined);

    return {
      ...raw,
      status: raw.status ?? status,
      job_state: raw.job_state ?? status,
      s3_url: raw.s3_url ?? topS3,
      data,
      error:
        raw.error ??
        (typeof nested['error'] === 'string' ? nested['error'] : undefined),
      message:
        raw.message ??
        (typeof nested['message'] === 'string' ? nested['message'] : undefined),
    };
  }

  private normalizeLiquidCargoStatusResponse(
    raw: LiquidCargoTableStatusResponse,
  ): LiquidCargoTableStatusResponse {
    return this.normalizeWrappedTableStatusResponse(
      raw as Record<string, unknown>,
    ) as LiquidCargoTableStatusResponse;
  }

  private normalizeHeatingTableStatusResponse(
    raw: HeatingTableStatusResponse,
  ): HeatingTableStatusResponse {
    return this.normalizeWrappedTableStatusResponse(
      raw as Record<string, unknown>,
    ) as HeatingTableStatusResponse;
  }

  private normalizeWrappedTableStatusResponse<
    T extends Record<string, unknown>,
  >(raw: T): T {
    const nested = raw['data'] as Record<string, unknown> | undefined;
    if (!nested || typeof nested !== 'object' || Array.isArray(nested)) {
      return raw;
    }

    const merged: Record<string, unknown> = { ...raw };
    const status =
      merged['status'] ??
      merged['job_state'] ??
      nested['status'] ??
      nested['job_state'];
    if (status != null && status !== '') {
      merged['status'] = merged['status'] ?? status;
      merged['job_state'] = merged['job_state'] ?? status;
    }

    const liftKeys = [
      'tables',
      's3_url',
      's3_path',
      'rows',
      'fill_note',
      'fillNote',
      'error',
      'message',
      'output',
      'thermal_parameters',
      'thermalParameters',
      'heating_conditions',
      'tank_details',
      'compartments',
      'compartment_metrics',
      'heating_table',
    ];
    for (const key of liftKeys) {
      if (merged[key] == null && nested[key] != null) {
        merged[key] = nested[key];
      }
    }

    if (merged['output'] == null) {
      const output: Record<string, unknown> = {};
      if (nested['tables']) {
        output['tables'] = nested['tables'];
      }
      if (nested['rows']) {
        output['rows'] = nested['rows'];
      }
      if (nested['fill_note'] != null) {
        output['fill_note'] = nested['fill_note'];
      }
      if (nested['fillNote'] != null) {
        output['fillNote'] = nested['fillNote'];
      }
      if (nested['compartments']) {
        output['compartments'] = nested['compartments'];
      }
      if (nested['compartment_metrics']) {
        output['compartment_metrics'] = nested['compartment_metrics'];
      }
      if (nested['heating_table']) {
        output['heating_table'] = nested['heating_table'];
      }
      if (nested['thermal_parameters']) {
        output['thermal_parameters'] = nested['thermal_parameters'];
      }
      if (nested['thermalParameters']) {
        output['thermalParameters'] = nested['thermalParameters'];
      }
      if (nested['heating_conditions']) {
        output['heating_conditions'] = nested['heating_conditions'];
      }
      if (nested['tank_details']) {
        output['tank_details'] = nested['tank_details'];
      }
      if (Object.keys(output).length > 0) {
        merged['output'] = output;
      }
    }

    if (merged['error'] == null && nested['error'] != null) {
      merged['error'] = nested['error'];
    }
    if (merged['message'] == null && nested['message'] != null) {
      merged['message'] = nested['message'];
    }

    return merged as T;
  }

  private resolveBoilerPdfPollState(
    response: BoilerPdfStatusResponse,
  ): { complete: boolean; failed: boolean; message?: string } {
    const status = String(response?.status ?? '').toLowerCase();
    if (status === 'failed' || status === 'error') {
      return {
        complete: false,
        failed: true,
        message:
          response?.error ||
          response?.message ||
          response?.data?.error ||
          'Boiler PDF processing failed.',
      };
    }
    if (status === 'completed' || status === 'success' || status === 'done') {
      if (!this.hasBoilerPdfPayload(response)) {
        const message =
          response?.error ||
          response?.message ||
          response?.data?.error ||
          'Boiler PDF processed but no data was returned.';
        return { complete: false, failed: true, message: String(message) };
      }
      return { complete: true, failed: false };
    }
    if (this.hasBoilerPdfPayload(response)) {
      return { complete: true, failed: false };
    }
    return { complete: false, failed: false };
  }

  private hasBoilerPdfPayload(response: BoilerPdfStatusResponse): boolean {
    const data = response?.data;
    if (!data) {
      return false;
    }
    return (
      data.boiler_capacity != null ||
      !!String(data.boiler_manufacturer ?? '').trim() ||
      data.max_efficiency != null ||
      data.steam_production_at_max_efficiency != null ||
      data.deck_steam_pressure != null
    );
  }

  private pollStatusUntilComplete<T>(
    fetch: () => Observable<T>,
    resolveState: (response: T) => {
      complete: boolean;
      failed: boolean;
      message?: string;
    },
    intervalMs: number,
    maxAttempts: number,
    timeoutMessage: string,
  ): Observable<T> {
    let attempts = 0;
    const poll = (): Observable<T> =>
      fetch().pipe(
        switchMap((response) => {
          const { complete, failed, message } = resolveState(response);
          if (failed) {
            return throwError(() => new Error(message || 'Processing failed.'));
          }
          if (complete) {
            return of(response);
          }
          attempts += 1;
          if (attempts >= maxAttempts) {
            return throwError(() => new Error(timeoutMessage));
          }
          return timer(intervalMs).pipe(switchMap(() => poll()));
        }),
      );
    return poll();
  }

  private resolveHeatingTablePollState(
    response: HeatingTableStatusResponse,
  ): { complete: boolean; failed: boolean; message?: string } {
    const state = String(
      response?.job_state ?? response?.status ?? '',
    ).toLowerCase();
    if (state === 'failed' || state === 'error') {
      return {
        complete: false,
        failed: true,
        message:
          response?.message ||
          response?.error ||
          'Heating table processing failed.',
      };
    }
    if (state === 'not_found') {
      return {
        complete: false,
        failed: true,
        message: response?.info || 'Tracking identifier does not exist.',
      };
    }
    if (
      state === 'completed' ||
      state === 'success' ||
      state === 'done'
    ) {
      if (!this.hasHeatingTablePayload(response)) {
        const message =
          response?.error ||
          response?.message ||
          'Heating table processing completed without data.';
        return { complete: false, failed: true, message: String(message) };
      }
      return { complete: true, failed: false };
    }
    if (this.hasHeatingTablePayload(response)) {
      return { complete: true, failed: false };
    }
    return { complete: false, failed: false };
  }

  private hasHeatingTablePayload(
    response: HeatingTableStatusResponse,
  ): boolean {
    const data = response?.data as Record<string, unknown> | undefined;

    const heatingConditions =
      response?.heating_conditions ??
      (data?.['heating_conditions'] as Record<string, unknown> | undefined);
    const hasHeatingConditions =
      heatingConditions &&
      typeof heatingConditions === 'object' &&
      !Array.isArray(heatingConditions) &&
      Object.keys(heatingConditions).length > 0;

    const tankDetails =
      response?.tank_details ??
      (data?.['tank_details'] as Array<unknown> | undefined) ??
      (data?.['tankDetails'] as Array<unknown> | undefined);
    const hasTankDetails = Array.isArray(tankDetails) && tankDetails.length > 0;

    if (hasHeatingConditions) {
      return hasTankDetails;
    }

    if (hasTankDetails) {
      return true;
    }

    const thermal =
      response?.thermal_parameters ??
      response?.thermalParameters ??
      (data?.['thermal_parameters'] as Record<string, unknown> | undefined) ??
      (data?.['thermalParameters'] as Record<string, unknown> | undefined);
    if (
      thermal &&
      typeof thermal === 'object' &&
      !Array.isArray(thermal) &&
      Object.keys(thermal).length > 0
    ) {
      return true;
    }

    const output = response?.output;
    if (
      output?.compartments?.length ||
      output?.compartment_metrics?.length ||
      output?.heating_table?.length
    ) {
      return true;
    }
    if (response?.tables?.[0]?.rows?.length) {
      return true;
    }
    if (output?.tables?.[0]?.rows?.length) {
      return true;
    }
    if (Array.isArray(output?.rows) && (output?.rows?.length ?? 0) > 0) {
      return true;
    }
    return false;
  }

  private resolveLiquidCargoPollState(
    response: LiquidCargoTableStatusResponse,
  ): { complete: boolean; failed: boolean; message?: string } {
    const state = String(
      response?.job_state ?? response?.status ?? '',
    ).toLowerCase();
    if (state === 'failed' || state === 'error') {
      return {
        complete: false,
        failed: true,
        message:
          response?.message ||
          response?.error ||
          'Liquid cargo table processing failed.',
      };
    }
    if (state === 'not_found') {
      return {
        complete: false,
        failed: true,
        message: response?.info || 'Tracking identifier does not exist.',
      };
    }
    if (
      state === 'completed' ||
      state === 'success' ||
      state === 'done'
    ) {
      if (!this.hasLiquidCargoPayload(response)) {
        const message =
          response?.error ||
          response?.message ||
          'Liquid cargo table processing completed without data.';
        return { complete: false, failed: true, message: String(message) };
      }
      return { complete: true, failed: false };
    }
    if (this.hasLiquidCargoPayload(response)) {
      return { complete: true, failed: false };
    }
    return { complete: false, failed: false };
  }

  private hasLiquidCargoPayload(
    response: LiquidCargoTableStatusResponse,
  ): boolean {
    const nested = response?.data as Record<string, unknown> | undefined;
    const nestedTables = nested?.['tables'];
    if (
      Array.isArray(nestedTables) &&
      (nestedTables[0] as { rows?: unknown[] })?.rows?.length
    ) {
      return true;
    }
    if (response?.tables?.[0]?.rows?.length) {
      return true;
    }
    if (response?.output?.tables?.[0]?.rows?.length) {
      return true;
    }
    if (response?.output?.rows?.length) {
      return true;
    }
    return false;
  }

  getCargoOptimizationConfig(imo: string): any | null {
    if (!imo) {
      return null;
    }
    const stored = localStorage.getItem(
      `${this.optimizationConfigKeyPrefix}${imo}`,
    );
    if (!stored) {
      return null;
    }
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  getGraphData(body: any) {
    const url = `${this.baseUrl}/cargoheating/fetch_data`;
    return this.http.post(url, body).pipe(catchError(this.errorHandler));
  }
  getUserManual() {
    const url = `${this.baseUrl}/cargoheating/download_user_manual`;
    return this.http
      .get(url, {
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler));
  }

  getVesselData(): Observable<any[]> {
    const url = `${this.baseUrl}/cargoheating/fetch_vessels`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getVoyageData(): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/voyage_data/get_data`;
    return this.http.get<any>(url).pipe(
      catchError(this.errorHandler)
    );
  }
  calibrateNow() {
    return this.http.post(
      `${this.baseUrl}/cargoheating/voyage_data/start_processing`,
      {}
    );
  }
  /** Progress of the gradient wise recalculation started by calibrateNow(). */
  getVoyageSyncStatus(): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/voyage_data/sync_status`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }




  deleteRowData(payload: any): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/delete_fleet_data`;
    return this.http
      .post(url, payload)
      .pipe(catchError(this.errorHandler));
  }

  postRequestForm(jsonData: any, files: File[]) {
    const url = `${this.baseUrl}/cargoheating/ingest_data`;
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append('file', file);
    });
    formData.append('json_data', JSON.stringify(jsonData));
    return this.http.post(url, formData).pipe(catchError(this.errorHandler));
  }

  printPdf(body: any) {
    const url = `${this.baseUrl}/cargoheating/print_report`;
    return this.http
      .post(url, body, { responseType: 'blob' })
      .pipe(catchError(this.errorHandler));
  }
  printSelection(body: any) {
    const url = `${this.baseUrl}/cargoheating/print_selection`;
    return this.http
      .post(url, body, { responseType: 'blob' })
      .pipe(catchError(this.errorHandler));
  }

  InsertVesselLog(formData: FormData) {
    const url = `${this.baseUrl}/cargoheating/ingest_master_data`;
    return this.http.post(url, formData).pipe(catchError(this.errorHandler));
  }

  postCalibratePlan(payload: any) {
    const url = `${this.baseUrl}/cargoheating/heatplan/calibrate_plan`;
    return this.http.post(url, payload).pipe(catchError(this.errorHandler));
  }
  postInsertFormData(payload: any) {
    const url = `${this.baseUrl}/cargoheating/heatplan/insert_formdata`;
    return this.http.post(url, payload).pipe(catchError(this.errorHandler));
  }
  postFleetData(payload: any) {
    return this.http.post(
      `${this.baseUrl}/cargoheating/fleet/post_data`,
      payload
    );
  }
  getFleetData(payload: any) {
    return this.http.post(
      `${this.baseUrl}/cargoheating/fleet/get_data`,
      payload
    );
  }

  generateFinalReport(formData: FormData): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/generate_finalReport`;
    return this.http.post(url, formData).pipe(
      catchError(this.errorHandler)
    );
  }
  //    generateFinalReports(payload: { voyage_id: string }): Observable<any> {
  //   const url = `${this.baseUrl}/cargoheating/generate_finalReport`;

  //   return this.http.post<any>(url, payload).pipe(
  //     catchError(this.errorHandler)
  //   );
  // }
  downloadTemperatureLog(payload: { voyage_id: string }) {

    const url = `${this.baseUrl}/cargoheating/final_report/get_temperature_log`;

    return this.http.post(url, payload, {
      responseType: 'blob'   // file download ke liye important
    });

  }
  /** Whether the final report can be built (itinerary + logs), without building it; a 400 lists `problems`. */
  checkFinalReport(voyageId: string): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/final_report/build_report`;
    const fd = new FormData();
    fd.append('voyage_id', voyageId);
    fd.append('check_only', 'true');
    return this.http.post(url, fd);
  }

  generateFinalReports(formData: FormData): Observable<any> {

    const url = `${this.baseUrl}/cargoheating/final_report/build_report`;

    return this.http.post<any>(url, formData).pipe(
      catchError(this.errorHandler)
    );

  }
  postHeatPlan(formData: FormData) {
    const url = `${this.baseUrl}/cargoheating/heatplan/create_plan`;
    return this.http.post(url, formData).pipe(catchError(this.errorHandler));
  }

  /** Validates temperature log tanks against the vessel's saved tank structure. */
  validateTankStructure(voyageId: string, heatplanNo: string | number, tempLog: File): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/voyage_data/validate_tank_structure`;
    const fd = new FormData();
    fd.append('voyage_id', String(voyageId));
    fd.append('heatplan_no', String(heatplanNo));
    fd.append('temp_log', tempLog);
    return this.http.post(url, fd);
  }

  /** Whether voyage monitoring has logs for the voyage, and the starting point a new heat plan would be built from. */
  getMonitoringSummary(voyageId: string): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/heatplan/monitoring_summary?voyage_id=${encodeURIComponent(voyageId)}`;
    return this.http.get(url);
  }

  /** Route waypoints for the heat plan leg. `start_date` (YYYY-MM-DD) anchors an at-port plan to the last logged day. */
  getHeatPlanRoute(payload: {
    voyage_id: string;
    heatplan_no: string | number;
    route_source: string;
    start_date?: string;
  }): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/heatplan/get_route`;
    return this.http.post(url, payload);
  }

  /** Weather (air / water temp, wind) for each route waypoint. */
  getHeatPlanWeather(payload: { voyage_id: string; heatplan_no: string | number; points: any[] }): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/heatplan/get_weather`;
    return this.http.post(url, payload);
  }

  postRowData(payload: any): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/database_filter`;
    return this.http.post(url, payload).pipe(catchError(this.errorHandler));
  }

  setFleetViewRowDat(data: any): void {
    this.fleetViewRowData$.next(data);
    if (data) {
      sessionStorage.setItem('ch-fleet-view-row-data', JSON.stringify(data));
    } else {
      sessionStorage.removeItem('ch-fleet-view-row-data');
    }
  }

  getFleetViewRowDat(): Observable<any> {
    if (!this.fleetViewRowData$.getValue()) {
      const stored = sessionStorage.getItem('ch-fleet-view-row-data');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          this.fleetViewRowData$.next(parsed);
        } catch { }
      }
    }
    return this.fleetViewRowData$.asObservable();
  }

  clearFleetViewRowDat(): void {
    this.fleetViewRowData$.next(null);
    sessionStorage.removeItem('ch-fleet-view-row-data');
  }

  /**
   * Remembers which list a voyage was opened from ('ongoing' | 'completed')
   * so voyage detail pages can render a source-aware Back button.
   * Persisted in sessionStorage so a page refresh keeps the correct destination.
   */
  setVoyageListSource(source: 'ongoing' | 'completed'): void {
    sessionStorage.setItem('ch-voyage-list-source', source);
  }

  getVoyageListSource(): 'ongoing' | 'completed' {
    return sessionStorage.getItem('ch-voyage-list-source') === 'completed'
      ? 'completed'
      : 'ongoing';
  }

  errorHandler(error: HttpErrorResponse) {
    return throwError(() => error || 'Server Error');
  }

  setHeatPlanData(data: any) {
    this.heatPlanData = data;
    this.heatPlanDataVersion++;
    if (data) {
      sessionStorage.setItem('ch-heat-plan-data', JSON.stringify(data));
    } else {
      sessionStorage.removeItem('ch-heat-plan-data');
    }
  }

  getHeatPlanData() {
    if (!this.heatPlanData) {
      const stored = sessionStorage.getItem('ch-heat-plan-data');
      if (stored) {
        try {
          this.heatPlanData = JSON.parse(stored);
        } catch { }
      }
    }
    return this.heatPlanData;
  }

  getHeatPlanDataVersion(): number {
    return this.heatPlanDataVersion;
  }

  clearHeatPlanData(): void {
    this.heatPlanData = null;
    sessionStorage.removeItem('ch-heat-plan-data');
  }

  getForecastDraftStorageKey(
    voyageId?: string | number,
    heatplanNo?: string | number,
  ): string {
    const voyage = voyageId ?? '';
    const plan = heatplanNo ?? '';
    return `ch-forecast-draft-${voyage}-${plan}`;
  }

  clearForecastDraftStorage(
    voyageId?: string | number,
    heatplanNo?: string | number,
  ): void {
    const draftKey = this.getForecastDraftStorageKey(voyageId, heatplanNo);
    if (!draftKey.endsWith('--')) {
      sessionStorage.removeItem(draftKey);
    }
  }

  /** Call before navigating to Forecast after Start Fresh (or any full replan). */
  markForecastDataAsFresh(): void {
    this.skipForecastDraftRestore = true;
  }

  /** Forecast page reads this once on load; defaults back to allowing draft restore. */
  consumeForecastDraftRestoreFlag(): boolean {
    const shouldRestore = !this.skipForecastDraftRestore;
    this.skipForecastDraftRestore = false;
    return shouldRestore;
  }

  setHeatPlanGraphRow(row: any): void {
    this.heatPlanGraphRow = row;
  }

  getHeatPlanGraphRow(): any {
    return this.heatPlanGraphRow;
  }

  clearHeatPlanGraphRow(): void {
    this.heatPlanGraphRow = null;
  }

  setHeatPlanGraphPayload(data: any): void {
    this.heatPlanGraphPayload = data;
  }

  getHeatPlanGraphPayload(): any {
    return this.heatPlanGraphPayload;
  }

  clearHeatPlanGraphPayload(): void {
    this.heatPlanGraphPayload = null;
  }

  setHeatPlanFormContext(context: any): void {
    this.heatPlanFormContext = context;
    if (context) {
      sessionStorage.setItem('ch-heat-plan-context', JSON.stringify(context));
    }
  }

  getHeatPlanFormContext(): any {
    if (this.heatPlanFormContext) {
      return this.heatPlanFormContext;
    }

    const stored = sessionStorage.getItem('ch-heat-plan-context');
    if (!stored) {
      return null;
    }

    try {
      this.heatPlanFormContext = JSON.parse(stored);
      return this.heatPlanFormContext;
    } catch {
      return null;
    }
  }

  clearHeatPlanFormContext(): void {
    this.heatPlanFormContext = null;
    sessionStorage.removeItem('ch-heat-plan-context');
  }

  postPivotData(jsonData: any) {
    const url = `${this.baseUrl}/cargoheating/fetch_pivot_data`;
    return this.http
      .post<any>(url, jsonData, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(this.errorHandler));
  }
  postPivotDatam(jsonData: any) {
    const url = `${this.baseUrl}/cargoheating/voyage_data/predict_drop`;
    return this.http
      .post<any>(url, jsonData, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(this.errorHandler));
  }
  fetchPivotDetailData(payload: any) {
    const url = `${this.baseUrl}/cargoheating/fetch_pivot_detail`;
    return this.http
      .post<any>(url, payload, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(this.errorHandler));
  }
  fetchPivotDetailDatam(payload: any) {
    const url = `${this.baseUrl}/cargoheating/voyage_data/predicted_voyage`;
    return this.http
      .post<any>(url, payload, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(this.errorHandler));
  }
  getDatabaseData(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/cargoheating/database_fetch`);
  }
  getAllClients(): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/getallclients`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getCalibratePlan(payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/cargoheating/heatplan/get_calibrate_plan`,
      payload
    );
  }

  /**
   * Load heat plan for graph view — same print_report API as Report Table download.
   * Does not call get_calibrate_plan / filter_request (not available for archived reports).
   */
  fetchHeatPlanChartRow(row: {
    ship_name?: string;
    ship_imo?: string | number;
    voyage_no?: string | number;
    hp_np?: string | number;
    hp_number?: string | number;
    voyage_id?: string | number;
    client?: string;
    voyage_month?: string;
  }): Observable<any> {
    const rowWithVoyage = {
      ...row,
      voyage_no: row.voyage_no ?? row.voyage_month,
    };
    return this.fetchHeatPlanFromCalibrate(rowWithVoyage).pipe(
      catchError(() => this.fetchHeatPlanFromPrintReport(rowWithVoyage)),
    );
  }

  /** Same endpoint as Report Table → Heat Plan download. */
  private fetchHeatPlanFromPrintReport(row: {
    ship_name?: string;
    ship_imo?: string | number;
    voyage_no?: string | number;
    hp_np?: string | number;
    hp_number?: string | number;
    voyage_month?: string;
  }): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/print_report`;
    const body = buildHeatPlanPrintPayload(row);

    return this.http
      .post(url, body, { responseType: 'blob' })
      .pipe(
        switchMap((blob) => {
          if (!blob?.size) {
            return throwError(() => new Error('Empty Heat Plan response'));
          }
          return from(
            parseHeatPlanPrintBlob(blob, {
              ship_name: row.ship_name,
              ship_imo: row.ship_imo,
            }),
          );
        }),
        catchError((err) => {
          if (err?.message) {
            return throwError(() => err);
          }
          return throwError(
            () => new Error('Heat Plan download failed. Use the same row that works in Report Table.'),
          );
        }),
      );
  }

  private fetchHeatPlanFromCalibrate(row: {
    ship_name?: string;
    ship_imo?: string | number;
    voyage_no?: string | number;
    hp_np?: string | number;
    hp_number?: string | number;
    voyage_id?: string | number;
    client?: string;
    voyage_month?: string;
  }): Observable<any> {
    const session = this.getSession();
    const voyageNo = row.voyage_no ?? row.voyage_month;
    const heatplanNo = row.hp_np ?? row.hp_number;
    const rowWithVoyage = { ...row, voyage_no: voyageNo };

    const loadPlan = (voyage_id: string | number, client: string) =>
      this.loadCalibratePlanForGraph(
        voyage_id,
        client || session,
        heatplanNo,
        rowWithVoyage,
      );

    if (row.voyage_id) {
      return loadPlan(row.voyage_id, row.client || session);
    }

    const filterPayloads = this.buildHeatPlanFilterPayloads(rowWithVoyage, session);

    return this.tryFilterRequests(filterPayloads).pipe(
      switchMap((matches) => {
        const match = this.pickVoyageForHeatPlan(matches, rowWithVoyage);
        if (!match?.voyage_id) {
          return throwError(() => new Error('Voyage not found'));
        }
        return loadPlan(match.voyage_id, match.client || row.client || session);
      }),
      catchError(() =>
        this.resolveVoyageFromRequestLists(rowWithVoyage).pipe(
          switchMap(({ voyage_id, client }) => loadPlan(voyage_id, client)),
        ),
      ),
    );
  }

  private loadCalibratePlanForGraph(
    voyage_id: string | number,
    client: string,
    heatplanNo: string | number | undefined,
    row: { ship_name?: string; ship_imo?: string | number },
  ): Observable<any> {
    const base = { voyage_id, client };
    const attempt = (planNo: string | number | undefined) => {
      if (planNo === undefined || planNo === null || planNo === '') {
        return throwError(() => new Error('Missing heat plan number'));
      }
      return this.getCalibratePlan({ ...base, heatplan_no: planNo }).pipe(
        map((res) => this.normalizeHeatPlanChartResponse(res, row)),
      );
    };

    return attempt(heatplanNo).pipe(
      catchError(() => {
        const numeric = Number(heatplanNo);
        if (Number.isFinite(numeric) && String(numeric) !== String(heatplanNo)) {
          return attempt(numeric);
        }
        return throwError(() => new Error('Calibrate plan not found'));
      }),
    );
  }

  private normalizeHeatPlanChartResponse(
    res: any,
    row: { ship_name?: string; ship_imo?: string | number },
  ): any {
    if (!res) {
      throw new Error('Empty heat plan response');
    }

    let table = res.response;
    if (typeof table === 'string') {
      table = JSON.parse(table);
    }
    if (!table?.columns?.length || !table?.data?.length) {
      throw new Error('Heat plan table missing in response');
    }

    return {
      ...res,
      vessel_name: res.vessel_name ?? row.ship_name,
      imo: res.imo ?? row.ship_imo,
      response: table,
    };
  }

  private buildHeatPlanFilterPayloads(
    row: {
      ship_name?: string;
      ship_imo?: string | number;
      voyage_no?: string | number;
      voyage_month?: string | number;
      client?: string;
    },
    session: string,
  ): Record<string, unknown>[] {
    const vesselName = row.ship_name?.trim();
    const vesselImo = Number(row.ship_imo);
    const voyageNo =
      row.voyage_no != null
        ? String(row.voyage_no)
        : row.voyage_month != null
          ? String(row.voyage_month)
          : '';
    const client = row.client || session;

    const payloads: Record<string, unknown>[] = [];

    if (vesselName && vesselImo && voyageNo) {
      payloads.push({
        vessel_name: vesselName,
        vessel_imo: vesselImo,
        voyage_no: voyageNo,
        client,
        session,
      });
    }
    if (vesselName && vesselImo && voyageNo) {
      payloads.push({ vessel_name: vesselName, vessel_imo: vesselImo, voyage_no: voyageNo });
    }
    if (vesselImo && voyageNo) {
      payloads.push({ vessel_imo: vesselImo, voyage_no: voyageNo, client, session });
      payloads.push({ vessel_imo: vesselImo, voyage_no: voyageNo });
    }
    if (vesselName && vesselImo) {
      payloads.push({ vessel_name: vesselName, vessel_imo: vesselImo, client, session });
    }
    if (vesselImo) {
      payloads.push({ vessel_imo: vesselImo, client, session });
    }

    return payloads;
  }

  private tryFilterRequests(
    payloads: Record<string, unknown>[],
  ): Observable<any[]> {
    if (!payloads.length) {
      return throwError(() => new Error('Voyage not found'));
    }

    const [head, ...tail] = payloads;
    return this.requestFormFilter(head).pipe(
      catchError(() => of([])),
      map((res) => (Array.isArray(res) ? res : [])),
      switchMap((matches) => {
        if (matches.length) {
          return of(matches);
        }
        return tail.length
          ? this.tryFilterRequests(tail)
          : throwError(() => new Error('Voyage not found'));
      }),
    );
  }

  private pickVoyageForHeatPlan(
    matches: any[],
    row: {
      ship_imo?: string | number;
      voyage_no?: string | number;
      voyage_month?: string | number;
      hp_np?: string | number;
      hp_number?: string | number;
      ship_name?: string;
    },
  ): any {
    if (!matches?.length) {
      return null;
    }

    const hp = String(row.hp_np ?? row.hp_number ?? '').trim();
    const voy = String(row.voyage_no ?? row.voyage_month ?? '').trim();
    const imo = String(row.ship_imo ?? '').trim();
    const name = (row.ship_name ?? '').trim().toLowerCase();

    if (hp) {
      const byHp = matches.find(
        (m) =>
          String(m.heatplan_no ?? m.heat_plan_no ?? m.hp_number ?? '').trim() ===
          hp,
      );
      if (byHp) {
        return byHp;
      }
    }

    if (voy && imo) {
      const byImoVoy = matches.find(
        (m) =>
          String(m.vessel_imo ?? m.imo ?? '').trim() === imo &&
          String(m.voyage_no ?? '').trim() === voy,
      );
      if (byImoVoy) {
        return byImoVoy;
      }
    }

    if (name && voy) {
      const byNameVoy = matches.find(
        (m) =>
          String(m.vessel_name ?? '')
            .trim()
            .toLowerCase() === name &&
          String(m.voyage_no ?? '').trim() === voy,
      );
      if (byNameVoy) {
        return byNameVoy;
      }
    }

    return matches[0];
  }

  private resolveVoyageFromRequestLists(row: {
    ship_name?: string;
    ship_imo?: string | number;
    voyage_no?: string | number;
    hp_np?: string | number;
    hp_number?: string | number;
    client?: string;
  }): Observable<{ voyage_id: string | number; client: string }> {
    const session = this.getSession();
    const username = localStorage.getItem('username');

    const fromList = (list: any[]) => {
      const filtered = this.filterDataByClient(list || [], username);
      const match =
        this.pickVoyageForHeatPlan(filtered, row) ||
        this.pickVoyageForHeatPlan(list || [], row);
      if (!match?.voyage_id) {
        return throwError(() => new Error('Voyage not found'));
      }
      return of({
        voyage_id: match.voyage_id,
        client: match.client || row.client || session,
      });
    };

    return this.specialVoyageApi().pipe(
      switchMap((completed) => fromList(completed)),
      catchError(() =>
        this.getallrequestsData().pipe(switchMap((running) => fromList(running))),
      ),
    );
  }

  changeVoyageStatus(payload: any) {
    return this.http.post(
      `${this.baseUrl}/cargoheating/heatplan/change_voyage_status`,
      payload
    );
  }
  // Add this method to your service file
  processVoyageLogs(formData: FormData): Observable<any> {
    return this.http.post(`${this.baseUrl}/cargoheating/voyage_data/process_logs`, formData);
  }

  insertDailyLogs(payload: {
    voyageId: string;
    tempLog: File;
  }): Observable<any> {
    const session = this.getSession();
    const formData = new FormData();

    formData.append('voyage_id', payload.voyageId);
    formData.append('temp_log', payload.tempLog, payload.tempLog.name);

    const url = `${this.baseUrl}/cargoheating/voyage_data/insert_daily_log?session=${encodeURIComponent(session)}`;
    return this.http.post(url, formData).pipe(catchError(this.errorHandler));
  }

  insertTankProfile(payload: {
    voyageId: string;
    tempLog: File;
  }): Observable<any> {
    const session = this.getSession();
    const formData = new FormData();

    formData.append('voyage_id', payload.voyageId);
    formData.append('temp_log', payload.tempLog, payload.tempLog.name);

    const url = `${this.baseUrl}/cargoheating/voyage_data/insert_tank_profile?session=${encodeURIComponent(session)}`;
    return this.http.post(url, formData).pipe(catchError(this.errorHandler));
  }

  validateDailyLogFile(payload: {
    voyageId: string;
    tempLog: File;
  }): Observable<any> {
    const session = this.getSession();
    const formData = new FormData();

    formData.append('voyage_id', payload.voyageId);
    formData.append('temp_log', payload.tempLog, payload.tempLog.name);
    formData.append('session', session);

    const url = `${this.baseUrl}/cargoheating/voyage_data/insert_daily_log/validate_file?session=${encodeURIComponent(session)}`;
    return this.http.post(url, formData).pipe(catchError(this.errorHandler));
  }

  getVoyageLogs(voyageId: string): Observable<any> {
    const session = this.getSession();
    const url = `${this.baseUrl}/cargoheating/voyage_data/get_voyage_logs?voyage_id=${encodeURIComponent(voyageId)}&session=${encodeURIComponent(session)}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  getTankProfile(voyageId: string): Observable<any> {
    const session = this.getSession();
    const url = `${this.baseUrl}/cargoheating/voyage_data/get_tank_profile?voyage_id=${encodeURIComponent(voyageId)}&session=${encodeURIComponent(session)}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  getCombinedVoyageData(voyageId: string): Observable<any> {
    const session = this.getSession();
    const url = `${this.baseUrl}/cargoheating/voyage_data/get_combined_voyage_data?voyage_id=${encodeURIComponent(voyageId)}&session=${encodeURIComponent(session)}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  deleteVoyageLogs(voyageId: string): Observable<any> {
    const session = this.getSession();
    const url = `${this.baseUrl}/cargoheating/voyage_data/delete_voyage_logs?session=${encodeURIComponent(session)}`;
    return this.http.delete<any>(url, { body: { voyage_id: voyageId } }).pipe(catchError(this.errorHandler));
  }

  uploadTankProfile(payload: Record<string, unknown>): Observable<any> {
    const session = this.getSession();
    const url = `${this.baseUrl}/cargoheating/voyage_data/upload_tank_profile?session=${encodeURIComponent(session)}`;
    return this.http.post<any>(url, payload).pipe(catchError(this.errorHandler));
  }

  saveOperatorRemarks(payload: {
    voyage_id: string;
    date: string;
    data: {
      remark: string;
    };
  }): Observable<any> {
    const session = this.getSession();
    const url = `${this.baseUrl}/cargoheating/voyage_data/operator_remarks?session=${encodeURIComponent(session)}`;
    return this.http.post<any>(url, payload).pipe(catchError(this.errorHandler));
  }

  getAvailableHeatplanDetails(voyageId: string): Observable<any> {
    const session = this.getSession();
    const url = `${this.baseUrl}/cargoheating/voyage_data/get_available_heatplan_details?voyage_id=${encodeURIComponent(voyageId)}&session=${encodeURIComponent(session)}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  getLogsOfHeatplan(voyageId: string, heatplanNo: number | string): Observable<any> {
    const session = this.getSession();
    const url = `${this.baseUrl}/cargoheating/voyage_data/get_logs_of_heatplan?voyage_id=${encodeURIComponent(voyageId)}&heatplan_no=${encodeURIComponent(String(heatplanNo))}&session=${encodeURIComponent(session)}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  getEtaInfo(voyageId: string): Observable<any> {
    const session = this.getSession();
    const url = `${this.baseUrl}/cargoheating/formdata/get_eta_info?voyage_id=${encodeURIComponent(voyageId)}&session=${encodeURIComponent(session)}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  insertEtaInfo(payload: {
    voyage_id: string;
    port_name: string;
    date: string;
  }): Observable<any> {
    const session = this.getSession();
    const url = `${this.baseUrl}/cargoheating/formdata/insert_eta_info?session=${encodeURIComponent(session)}`;
    return this.http.post<any>(url, payload).pipe(catchError(this.errorHandler));
  }



  getallrequestsData(): Observable<any[]> {
    const session = this.getSession();
    const url = `${this.baseUrl}/cargoheating/heatplan/fetch_running_requests?session=${encodeURIComponent(session)}`;
    console.log('Running Requests API:', url);
    return this.http.get<any[]>(url);
  }

  getVoyageBreakup(): Observable<any> {
    const session = this.getSession();
    const url = `${this.baseUrl}/cargoheating/get_voyage_breakup?session=${encodeURIComponent(session)}`;
    console.log('Voyage API URL:', url);
    return this.http.get<any>(url);
  }

  getSession(): string {
    const username = localStorage.getItem('username') || '';
    if (!username || this.isMerakiOperations(username)) {
      return 'admin';
    }
    const sessionByUser: Record<string, string> = {
      'Bunge Shipping': 'Bunge SA',
      'Elite Shipping': 'Elite Tankships',
      'Eiger Shipping': 'Northwell',
      'Zeronorth ZN': 'zeronorth',
      'Alcor Shipping': 'alcor',
      'EMZoil Shipping': 'emz',
      'Ethiopian Shipping & Logistics': 'ethiopian',
    };
    if (sessionByUser[username]) {
      return sessionByUser[username];
    }
    if (username.toLowerCase().includes('bunge')) {
      return 'Bunge SA';
    }
    return username;
  }

  isMerakiOperations(username?: string | null): boolean {
    return (username ?? localStorage.getItem('username')) === 'Meraki - Operations';
  }

  isCargoHeatingClientLogin(username?: string | null): boolean {
    const user = username ?? localStorage.getItem('username');
    return !!user && !this.isMerakiOperations(user);
  }

  getCargoHeatingClientAllowedRoutes(): string[] {
    return ['ch-allRequests', 'ch-reportTable', 'ch-finalReport', 'ch-benchmark', 'ch-requestForm'];
  }

  getCargoHeatingClientLandingRoute(): string {
    return '/ch-allRequests';
  }

  filterDataByClient<T extends { client?: string }>(
    data: T[],
    username?: string | null,
  ): T[] {
    if (!data?.length) {
      return [];
    }
    const user = username ?? localStorage.getItem('username');
    if (this.isMerakiOperations(user)) {
      return data;
    }
    const filterFn = this.getClientFilter(user);
    if (!filterFn) {
      return this.isCargoHeatingClientLogin(user) ? [] : data;
    }
    return data.filter(filterFn);
  }

  redirectCargoHeatingClientIfRestricted(router: Router): void {
    if (!this.isCargoHeatingClientLogin()) {
      return;
    }
    const allowedRoutes = this.getCargoHeatingClientAllowedRoutes();
    const path = router.url.split('?')[0].replace(/^\//, '');
    const root = path.split('/')[0];
    const isRestrictedCargoHeatingRoute =
      root.startsWith('ch-') || root === 'voyage-metrics' || root === 'detailed-view';

    if (isRestrictedCargoHeatingRoute && !allowedRoutes.includes(root)) {
      router.navigate([this.getCargoHeatingClientLandingRoute()]);
    }
  }

  private getClientFilter(
    username: string | null,
  ): ((item: { client?: string }) => boolean) | undefined {
    if (!username) {
      return undefined;
    }
    const clientFilters: Record<string, (item: { client?: string }) => boolean> = {
      'Eiger Shipping': (item) =>
        !!item.client &&
        (item.client === 'Northwell' || item.client.toLowerCase() === 'eiger'),
      'Zeronorth ZN': (item) =>
        !!item.client && item.client.toLowerCase() === 'zeronorth',
      'Elite Shipping': (item) =>
        !!item.client &&
        (item.client === 'elite tankship dmcc' ||
          item.client.toLowerCase() === 'elite' ||
          item.client.toLowerCase() === 'elite_tankships' ||
          item.client.toLowerCase() === 'elite tankships'),
      'Bunge Shipping': (item) =>
        !!item.client &&
        (item.client.toLowerCase() === 'bunge_sa' ||
          item.client.toLowerCase() === 'bunge sa'),
      'Alcor Shipping': (item) =>
        !!item.client && item.client.toLowerCase() === 'alcor chartering dmcc',
      'EMZoil Shipping': (item) =>
        !!item.client &&
        (item.client.toLowerCase() === 'emz oil' ||
          item.client.toLowerCase() === 'emzoil fzco' ||
          item.client.toLowerCase().includes('emz')),
      'Ethiopian Shipping & Logistics': (item) =>
        item.client?.toLowerCase() === 'ethiopian shipping & logistics',
    };
    return clientFilters[username];
  }
  specialVoyageApi(): Observable<any> {

    const session = this.getSession();

    const url = `${this.baseUrl}/cargoheating/heatplan/fetch_completed_requests?session=${encodeURIComponent(session)}`;

    console.log("Special Voyage API:", url);

    return this.http.get<any>(url);

  }

  myrequestdataInsert(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/requestform_input`;
    return this.http
      .post<any>(url, jsonData, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(this.errorHandler));
  }
  requestFormFilter(payload: any): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/heatplan/filter_request`;
    return this.http
      .post<any>(url, payload, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(this.errorHandler));
  }
  getPlanWiseData(payload: any): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/cargoheating/heatplan/get_planwise_data`,
      payload
    );
  }
  postFormData(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/database_insert`;
    return this.http
      .post<any>(url, jsonData, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(this.errorHandler));
  }
  fetchBenchmarkData(): Observable<any> {

    const session = this.getSession();

    const url = `${this.baseUrl}/cargoheating/benchmark/get_stats?session=${encodeURIComponent(session)}`;

    console.log("Benchmark API:", url);

    return this.http
      .get<any>(url, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(this.errorHandler));
  }

  saveBenchmarkOperatorRemarks(payload: {
    voyage_id: string;
    data: {
      comment: string;
    };
  }): Observable<any> {
    const session = this.getSession();
    const url = `${this.baseUrl}/cargoheating/benchmark/operator_remarks?session=${encodeURIComponent(session)}`;
    return this.http
      .post<any>(url, payload, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(this.errorHandler));
  }

  generatebenchmarkReport(payload: any): Observable<Blob> {
    const url = `${this.baseUrl}/cargoheating/benchmark_report`;
    return this.http
      .post(url, payload, {
        responseType: 'blob',
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        catchError((error) => {
          console.error('benchmark_report error', error);
          return throwError(error);
        })
      );
  }

  /** Benchmark PDF for a single client (Performance Benchmarking screen). */
  getBenchmarkReport(client: string): Observable<Blob> {
    const url = `${this.baseUrl}/cargoheating/benchmark/get_report`;
    return this.http
      .get(url, {
        params: { client },
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler));
  }

  pullTemplatePDF(filename: string): Observable<Blob> {
    const url = `${this.baseUrl}/cargoheating/pull_template`;
    const payload = { filename };
    return this.http
      .post(url, payload, {
        responseType: 'blob',
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        catchError((error) => {
          console.error('pull_template error', error);
          return throwError(error);
        })
      );
  }
  postSubmitHeatPlan(payload: any): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/heatplan/submit_plan`;
    return this.http
      .post<any>(url, payload, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(this.errorHandler));
  }

  postResetHeatPlan(payload: any): Observable<any> {
    return this.http.post('/cargoheating/reset_heat_plan', payload);
  }
  createUniqueNumber(payload: any) {
    const url = `${this.baseUrl}/cargoheating/create_unique_no`;
    return this.http
      .post<any>(url, payload, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(this.errorHandler));
  }
  postPrintSave(formData: any) {
    const url = `${this.baseUrl}/cargoheating/ingest_data`;
    return this.http.post(url, formData).pipe(catchError(this.errorHandler));
  }
  postreportTableData(body: any) {
    const url = `${this.baseUrl}/cargoheating/report_insert`;
    return this.http.post(url, body).pipe(catchError(this.errorHandler));
  }
  getreportTableData() {
    const url = `${this.baseUrl}/cargoheating/report_fetch`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getFinalReportTableData() {
    const url = `${this.baseUrl}/cargoheating/final_report/get_report`;
    return this.http.get<any>(url).pipe(
      catchError(this.errorHandler)
    );
  }
  printFinalReport(body: any) {
    const url = `${this.baseUrl}/cargoheating/print_final_report`;
    return this.http
      .post(url, body, { responseType: 'blob' })
      .pipe(catchError(this.errorHandler));
  }
  getCompletedVoyage(voyageId: string) {

    const url = `${this.baseUrl}/cargoheating/voyage_data/get_completed_voyage`;

    const payload = {
      voyage_id: voyageId
    };

    return this.http.post<any>(url, payload).pipe(
      catchError(this.errorHandler)
    );

  }
  getVoyageBasicDetails(): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/voyage_data/get_voyage_basic_details`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  getVoyageWiseAnalysis(voyageId: string): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/voyage_data/voyage_wise_analysis?voyage_id=${voyageId}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  createHeatPlan(payload: any): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/cargoheating/heatplan/create_request`,
      payload
    );
  }

  refreshGradientMap(voyageId: string | number): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/gradient/refresh_gradient_map`;
    return this.http
      .get<any>(url, {
        params: { voyage_id: String(voyageId) },
      })
      .pipe(catchError(this.errorHandler));
  }

  updateAllGradients(): Observable<any> {
    const url = `${this.baseUrl}/cargoheating/voyage_data/update_all_gradients`;
    return this.http.get<any>(url).pipe(
      catchError(this.errorHandler)
    );
  }
extractBoilerCurveFromImage(imageFile: File, prompt?: string): Observable<any> {
    const formData = new FormData();
    formData.append('image', imageFile, imageFile.name);
    if (prompt) {
      formData.append('prompt', prompt);
    }

    const url = `${this.appsBaseUrl}/scperformance/extract_boiler_curve_vision`;
    return this.http.post<any>(url, formData).pipe(
      catchError(this.errorHandler)
    );
  }

}

